import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './SchoolPage.css';

const SchoolPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [school, setSchool] = useState(null);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingToCart, setAddingToCart] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [isCartSidebarOpen, setIsCartSidebarOpen] = useState(true);
  const [showMobileCartButton, setShowMobileCartButton] = useState(false);
  const [removingItem, setRemovingItem] = useState({});

  useEffect(() => {
    fetchSchoolData();
    if (user) {
      fetchCart();
    }
  }, [id, user]);

  useEffect(() => {
    // Show mobile cart button if cart has items on mobile
    if (user && cartItems.length > 0 && window.innerWidth <= 768) {
      setShowMobileCartButton(true);
    } else if (cartItems.length === 0) {
      setShowMobileCartButton(false);
    }
  }, [cartItems, user]);

  useEffect(() => {
    // Listen for cart updates
    const handleCartUpdate = () => {
      if (user) {
        fetchCart();
      }
    };
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [user]);

  const fetchSchoolData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/schools/${id}`);
      setSchool(response.data.school);
      setKits(response.data.kits);
      setError('');
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || 
        (err.response?.status === 403 
          ? 'You can only access your assigned school. Please navigate to your school from the schools directory.'
          : err.response?.data?.error || 'Failed to load school data. Please try again.');
      setError(errorMessage);
      console.error(err);
      
      // Redirect to schools page on error
      setTimeout(() => {
        navigate('/schools');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    if (!user) return;
    
    try {
      setCartLoading(true);
      const response = await api.get('/cart');
      setCartItems(response.data.cart || []);
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      setCartItems([]);
    } finally {
      setCartLoading(false);
    }
  };

  const handleAddToCart = async (kitId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setAddingToCart({ ...addingToCart, [kitId]: true });

    try {
      await api.post('/cart', { kit_id: kitId, quantity: 1 });
      // Fetch updated cart to show in sidebar
      await fetchCart();
      // Open sidebar on desktop when item is added
      setIsCartSidebarOpen(true);
      // Show mobile cart button on mobile
      if (window.innerWidth <= 768) {
        setShowMobileCartButton(true);
      }
      // Dispatch event to update cart badge in header
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || err.response?.data?.error || 'Failed to add item to cart. Please try again.';
      alert(errorMessage);
      
      // Redirect to schools page on error
      if (err.response?.status >= 500) {
        setTimeout(() => {
          navigate('/schools');
        }, 3000);
      }
    } finally {
      setAddingToCart({ ...addingToCart, [kitId]: false });
    }
  };

  const calculateCartTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + parseFloat(item.price || 0) * (item.quantity || 1);
    }, 0);
  };

  const handleBuyNow = () => {
    navigate('/checkout');
  };

  const handleRemoveItem = async (cartId) => {
    if (!window.confirm('Are you sure you want to remove this item from your cart?')) {
      return;
    }

    setRemovingItem({ ...removingItem, [cartId]: true });
    try {
      await api.delete(`/cart/${cartId}`);
      await fetchCart();
      // Dispatch event to update cart badge in header
      window.dispatchEvent(new Event('cartUpdated'));
      
      // Hide mobile cart button if cart is empty
      const updatedCart = cartItems.filter(item => item.id !== cartId);
      if (updatedCart.length === 0 && window.innerWidth <= 768) {
        setShowMobileCartButton(false);
      }
    } catch (err) {
      const errorMessage = err.userFriendlyMessage || err.response?.data?.error || 'Failed to remove item. Please try again.';
      alert(errorMessage);
    } finally {
      setRemovingItem({ ...removingItem, [cartId]: false });
    }
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="page">
          <div className="container">
            <div className="loading">Loading school details...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !school) {
    return (
      <div className="App">
        <Header />
        <div className="page">
          <div className="container">
            <div className="error">
              {error || 'School not found'}
              {user && user.user_type === 'parent' && (
                <div style={{ marginTop: '1rem' }}>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => navigate('/schools')}
                  >
                    Go to Your School
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className={`page school-page-with-cart ${user && cartItems.length > 0 ? 'has-cart-sidebar' : ''}`}>
        <div className="container">
          <div className="school-header">
            {school.logo_url && (
              <img 
                src={school.logo_url} 
                alt={school.name} 
                className="school-logo-large"
                onError={(e) => {
                  // Hide logo if it fails to load
                  e.target.style.display = 'none';
                }}
              />
            )}
            <div className="school-info">
              <h1 className="school-name">{school.name}</h1>
              <p className="school-board">{school.board_type}</p>
              <p className="school-location">{school.city}</p>
              {school.address && <p className="school-address">{school.address}</p>}
            </div>
          </div>

          <h2 className="section-title">Available Book Kits</h2>

          {kits.length === 0 ? (
            <div className="no-kits">No book kits available for this school yet.</div>
          ) : (
            <div className="kits-grid">
              {kits.map((kit, index) => {
                // Use a different book image for all standards
                const sharedImageUrl = 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=300&fit=crop&q=80';
                return (
                <div key={kit.id} className="kit-card" style={{ animationDelay: `${index * 0.1}s` }}>
                  <img 
                    src={sharedImageUrl} 
                    alt={kit.name} 
                    className="kit-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="kit-image-placeholder" style={{ display: 'none' }}>
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                    </svg>
                  </div>
                  <div className="kit-content">
                    <h3>{formatStandard(kit.standard)}</h3>
                    <p className="kit-name">{kit.name}</p>
                    {kit.description && <p className="kit-description">{kit.description}</p>}
                    
                    {kit.books && kit.books.length > 0 && (
                      <div className="kit-books">
                        <h4>Books Included:</h4>
                        <ul>
                          {kit.books.map((book, idx) => (
                            <li key={idx}>
                              <strong>{book.subject}:</strong> {book.title} ({book.publisher})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="kit-footer">
                      <div className="kit-price">₹{parseFloat(kit.price).toFixed(2)}</div>
                      <button
                        className="btn btn-primary btn-add-to-cart-icon"
                        onClick={() => handleAddToCart(kit.id)}
                        disabled={!kit.is_available || addingToCart[kit.id]}
                        aria-label={addingToCart[kit.id] ? 'Adding to cart...' : kit.is_available ? 'Add to cart' : 'Out of stock'}
                        title={addingToCart[kit.id] ? 'Adding...' : kit.is_available ? 'Add to Cart' : 'Out of Stock'}
                      >
                        {addingToCart[kit.id] ? (
                          <span className="cart-icon-spinner">⟳</span>
                        ) : kit.is_available ? (
                          <svg className="cart-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1"></circle>
                            <circle cx="20" cy="21" r="1"></circle>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                          </svg>
                        ) : (
                          <span className="cart-icon-disabled">✕</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>


        {/* Cart Sidebar - Hidden on mobile, show only on desktop */}
        {user && cartItems.length > 0 && (
          <div className={`cart-sidebar ${isCartSidebarOpen ? 'open' : ''} mobile-cart-sidebar-hidden`}>
              <div className="cart-sidebar-header">
                <div className="cart-header-content">
                  <h3>🛒 Your Cart</h3>
                  <span className="cart-item-count">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</span>
                </div>
                <button 
                  className="cart-sidebar-close"
                  onClick={() => setIsCartSidebarOpen(false)}
                  aria-label="Close cart"
                >
                  ✕
                </button>
              </div>
              {/* Drag handle for mobile */}
              <div className="cart-sidebar-drag-handle"></div>
            
            <div className="cart-sidebar-items">
              {cartLoading ? (
                <div className="cart-sidebar-loading">Loading...</div>
              ) : (
                cartItems.map((item) => (
                  <div key={item.id} className="cart-sidebar-item">
                    <div className="cart-item-info">
                      <div className="cart-item-name">
                        {item.kit_name || `Standard ${item.standard || 'N/A'}`}
                      </div>
                      <div className="cart-item-details">
                        <span className="cart-item-quantity">Qty: {item.quantity}</span>
                        <span className="cart-item-price">₹{parseFloat(item.price || 0).toFixed(2)}</span>
                        <button
                          className="cart-item-delete"
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={removingItem[item.id]}
                          aria-label="Remove item from cart"
                          title="Remove item"
                        >
                          {removingItem[item.id] ? (
                            <span className="delete-icon-spinner">⟳</span>
                          ) : (
                            <span className="delete-icon">🗑️</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="cart-sidebar-footer">
              <div className="cart-sidebar-total">
                <span className="total-label">Total:</span>
                <span className="total-amount">₹{calculateCartTotal().toFixed(2)}</span>
              </div>
              <button 
                className="btn btn-primary btn-buy-now"
                onClick={handleBuyNow}
              >
                Buy Now
              </button>
            </div>
          </div>
        )}

        {/* Mobile Floating Cart Button - goes to cart so user can modify quantity before checkout */}
        {user && showMobileCartButton && cartItems.length > 0 && (
          <button 
            className="mobile-floating-cart-button"
            onClick={() => navigate('/cart')}
            aria-label="View cart and modify order"
          >
            <span className="mobile-cart-icon">🛒</span>
            {cartItems.length > 0 && (
              <span className="mobile-cart-badge">{cartItems.length > 99 ? '99+' : cartItems.length}</span>
            )}
          </button>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SchoolPage;

